// src/utils/clearFirestoreCache.js
//
// Fail-safe Firestore cache reset primitives. A blocked IndexedDB delete is
// NOT a successful delete: while the request remains pending, every later
// Firestore open for the origin queues behind it. So the delete runs only under
// proof: exactly one persistent client (this tab), pending writes drained, that
// client terminated, inventory re-proven empty (C-09). Since 2026-09-05 the tabs
// arrange that proof among themselves (src/services/backgroundRefresh.js,
// src/utils/cacheRefreshPolicy.js): a tab nobody is using parks in memory mode
// to release the database, and whichever tab proves it is alone runs the reset.
// No user is ever asked to close tabs; no banner.

export const FIRESTORE_MEMORY_FALLBACK_KEY = 'gse_firestore_memory_fallback'
export const FIRESTORE_CACHE_RESET_MARKER_KEY = 'gse_firestore_cache_reset_in_progress'
export const FIRESTORE_CACHE_BOOT_TIMEOUT_MS = 8000
// Auth initialization gets its own, longer budget. The 8s cache budget is an
// IndexedDB-wedge tripwire, but auth init also includes getRedirectResult()
// network round-trips — on mobile/cellular those routinely need more than
// whatever the version check left over, and sharing one deadline sent healthy
// phones into "Startup recovery mode" every boot.
export const AUTH_BOOTSTRAP_TIMEOUT_MS = 20000

const FIRESTORE_DATABASE_PREFIX = 'firestore/'
const FIRESTORE_LOCAL_STORAGE_PREFIX = 'firestore_'
const FIRESTORE_CLIENT_KEY_PREFIX = 'firestore_clients_'
const FIRESTORE_MUTATION_KEY_PREFIX = 'firestore_mutations_'
// If a browser abandons an uncancellable IDB delete request, keeping later
// tabs memory-only is safer than reopening persistence behind that request.
// Successful/failed workers remove the marker immediately; this long TTL is
// only the crash failsafe.
const RESET_MARKER_TTL_MS = 24 * 60 * 60 * 1000
const PENDING_WRITE_DRAIN_TIMEOUT_MS = 6000
const WINDOW_NAME_RECOVERY_PREFIX = '__gse_firestore_recovery__:'
// WHICH SIGNAL SAYS A PEER IS ALIVE (measured in production 2026-09-05, SDK 10.13 /
// @firebase/firestore 4.7): the IndexedDB `clientMetadata` record. Every persistent client
// rewrites its own entry's `updateTimeMs` every 4 s (CLIENT_METADATA_REFRESH_INTERVAL_MS), and
// on shutdown deletes it. The localStorage `firestore_clients_*` entry is NOT a heartbeat: it is
// written on start and whenever the tab's active query targets change, so an idle live tab's
// entry ages indefinitely (a live tab's entry was watched going past 60 s). C-09 counted those
// entries and pruned the "stale" ones, which both mis-counted live peers and, since the SDK
// treats a removed entry as "that client left", could make the primary drop targets a live
// secondary still needed. Neither happens here: the count reads clientMetadata.
//
// Threshold: a hidden Chrome tab under intensive throttling wakes about once a minute, so its
// metadata can look 60-70 s old while alive; a frozen iOS tab or a killed one stops for good.
// Three minutes is far past the throttled cadence and still short enough that a dead tab does
// not hold up a rebuild for long. (The SDK itself uses 5 s for primary eligibility and 30 min
// before it garbage-collects a client's metadata; neither is our question.)
export const FIRESTORE_CLIENT_LIVE_MAX_AGE_MS = 3 * 60 * 1000
export const FIRESTORE_DB_OPEN_TIMEOUT_MS = 2000
const FIRESTORE_CLIENT_METADATA_STORE = 'clientMetadata'

let volatileRecoveryState = null

function knownFirestoreDatabases(projectId) {
  const safeProjectId =
    typeof projectId === 'string' && /^[a-z0-9][a-z0-9-]{2,62}$/.test(projectId)
      ? projectId
      : 'gse-main-site'
  return [`firestore/[DEFAULT]/${safeProjectId}/main`, `firestore/[DEFAULT]/${safeProjectId}`]
}

function browserStorage(name) {
  try {
    return typeof window !== 'undefined' ? window[name] : null
  } catch {
    return null
  }
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

export class CacheBootstrapTimeoutError extends Error {
  constructor(timeoutMs) {
    super(`Cache bootstrap exceeded ${timeoutMs}ms`)
    this.name = 'CacheBootstrapTimeoutError'
    this.code = 'cache-bootstrap-timeout'
    this.timeoutMs = timeoutMs
  }
}

export function withCacheBootstrapTimeout(
  promise,
  timeoutMs = FIRESTORE_CACHE_BOOT_TIMEOUT_MS,
  { setTimeoutFn = setTimeout, clearTimeoutFn = clearTimeout } = {}
) {
  let timeoutId
  const timeout = new Promise((resolve, reject) => {
    timeoutId = setTimeoutFn(() => reject(new CacheBootstrapTimeoutError(timeoutMs)), timeoutMs)
  })
  return Promise.race([Promise.resolve(promise), timeout]).finally(() => clearTimeoutFn(timeoutId))
}

export function createCacheBootstrapDeadline({
  timeoutMs = FIRESTORE_CACHE_BOOT_TIMEOUT_MS,
  now = () => Date.now(),
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
} = {}) {
  const expiresAt = now() + timeoutMs
  const remainingMs = () => Math.max(1, Math.ceil(expiresAt - now()))
  return {
    remainingMs,
    run(promise) {
      return withCacheBootstrapTimeout(promise, remainingMs(), { setTimeoutFn, clearTimeoutFn })
    },
  }
}

export function getFirestoreMemoryFallback(storage = browserStorage('sessionStorage')) {
  let raw = null
  try {
    raw = storage?.getItem(FIRESTORE_MEMORY_FALLBACK_KEY) || null
  } catch {
    raw = null
  }
  if (!raw) {
    try {
      if (window.name.startsWith(WINDOW_NAME_RECOVERY_PREFIX)) {
        raw = window.name.slice(WINDOW_NAME_RECOVERY_PREFIX.length)
      }
    } catch {
      raw = null
    }
  }
  const parsed = safeJsonParse(raw) || volatileRecoveryState
  if (!parsed || parsed.active !== true) return null
  return {
    active: true,
    reasonCode: String(parsed.reasonCode || 'cache-recovery'),
    message: String(
      parsed.message ||
        'Persistent offline cache is temporarily disabled for this tab while Firestore recovers.'
    ),
    startedAt: String(parsed.startedAt || ''),
  }
}

export function isFirestoreCacheResetInProgress(
  storage = browserStorage('localStorage'),
  now = Date.now()
) {
  try {
    const marker = safeJsonParse(storage?.getItem(FIRESTORE_CACHE_RESET_MARKER_KEY))
    if (!marker || Number(marker.expiresAt) <= now) {
      storage?.removeItem(FIRESTORE_CACHE_RESET_MARKER_KEY)
      return false
    }
    return true
  } catch {
    return false
  }
}

export function shouldUseMemoryFirestoreCache(
  storage = browserStorage('sessionStorage'),
  sharedStorage = browserStorage('localStorage')
) {
  return (
    getFirestoreMemoryFallback(storage)?.active === true ||
    isFirestoreCacheResetInProgress(sharedStorage)
  )
}

export function activateFirestoreMemoryFallback(
  reasonCode,
  message,
  storage = browserStorage('sessionStorage')
) {
  const state = {
    active: true,
    reasonCode: String(reasonCode || 'cache-recovery'),
    message: String(
      message ||
        'Persistent offline cache is temporarily disabled for this tab while Firestore recovers.'
    ),
    startedAt: new Date().toISOString(),
  }
  volatileRecoveryState = state
  const serialized = JSON.stringify(state)
  let stored = false
  try {
    if (storage) {
      storage.setItem(FIRESTORE_MEMORY_FALLBACK_KEY, serialized)
      stored = true
    }
  } catch {
    stored = false
  }
  if (!stored) {
    try {
      window.name = `${WINDOW_NAME_RECOVERY_PREFIX}${serialized}`
    } catch {
      // The in-memory copy still lets the current document mount a banner.
    }
  }
  return state
}

export function listActiveFirestoreClientKeys(storage = browserStorage('localStorage')) {
  const snapshot = inspectFirestorePersistenceState(storage)
  return { status: snapshot.status, keys: snapshot.clientKeys, error: snapshot.error }
}

/**
 * The SDK's localStorage bookkeeping. `mutationKeys` (one per unsynced write batch, any tab) is
 * the only part used as proof: a dead tab's unsynced writes are customer data and block every
 * delete until a persistent client syncs them. `clientKeys` is informational (see the note on
 * liveness above); never prune it, the SDK reads a removed entry as "that client left".
 */
export function inspectFirestorePersistenceState(storage = browserStorage('localStorage')) {
  if (!storage) {
    return { status: 'unavailable', clientKeys: [], mutationKeys: [] }
  }
  try {
    const clientKeys = []
    const mutationKeys = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith(FIRESTORE_CLIENT_KEY_PREFIX)) clientKeys.push(key)
      if (key?.startsWith(FIRESTORE_MUTATION_KEY_PREFIX)) mutationKeys.push(key)
    }
    return { status: 'ok', clientKeys, mutationKeys }
  } catch (error) {
    return { status: 'unavailable', clientKeys: [], mutationKeys: [], error }
  }
}

function firestoreMainDatabaseName(projectId) {
  return knownFirestoreDatabases(projectId)[0]
}

/**
 * Count the live Firestore clients of this origin from the SDK's own `clientMetadata` records.
 *
 *   status 'ok'           `live` = clientIds refreshed within maxAgeMs, `stale` = the rest
 *          'no-database'  the Firestore database does not exist (nothing to hold)
 *          'blocked'      the database could not be opened in time: a delete is pending on it
 *                         and every later open queues behind that request (C-09). Never alone.
 *          'unavailable'  no indexedDB.databases() (never open blindly: opening a database that
 *                         does not exist would CREATE an empty one the SDK cannot migrate), or
 *                         the read failed. Never alone.
 */
export async function countLiveFirestoreClients({
  indexedDBApi = typeof indexedDB !== 'undefined' ? indexedDB : null,
  projectId = 'gse-main-site',
  now = Date.now(),
  maxAgeMs = FIRESTORE_CLIENT_LIVE_MAX_AGE_MS,
  openTimeoutMs = FIRESTORE_DB_OPEN_TIMEOUT_MS,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
} = {}) {
  if (!indexedDBApi?.open || typeof indexedDBApi.databases !== 'function') {
    return { status: 'unavailable', live: [], stale: [] }
  }
  const name = firestoreMainDatabaseName(projectId)
  let listed
  try {
    listed = (await indexedDBApi.databases()).some(database => database?.name === name)
  } catch (error) {
    return { status: 'unavailable', live: [], stale: [], error }
  }
  if (!listed) return { status: 'no-database', live: [], stale: [] }

  return new Promise(resolve => {
    let settled = false
    let timer = null
    const finish = result => {
      if (settled) return
      settled = true
      if (timer) clearTimeoutFn(timer)
      resolve(result)
    }
    timer = setTimeoutFn(() => finish({ status: 'blocked', live: [], stale: [] }), openTimeoutMs)
    let request
    try {
      request = indexedDBApi.open(name)
    } catch (error) {
      finish({ status: 'unavailable', live: [], stale: [], error })
      return
    }
    request.onblocked = () => finish({ status: 'blocked', live: [], stale: [] })
    request.onerror = () =>
      finish({ status: 'unavailable', live: [], stale: [], error: request.error || null })
    request.onupgradeneeded = () => {
      // Only reachable if the database vanished between the listing and the open: do not
      // create it. Abort the upgrade; the open then fails and we report unavailable.
      try {
        request.transaction?.abort()
      } catch {
        // the error handler reports either way
      }
    }
    request.onsuccess = () => {
      const db = request.result
      try {
        if (!db.objectStoreNames.contains(FIRESTORE_CLIENT_METADATA_STORE)) {
          db.close()
          finish({ status: 'unavailable', live: [], stale: [] })
          return
        }
        const store = db
          .transaction(FIRESTORE_CLIENT_METADATA_STORE, 'readonly')
          .objectStore(FIRESTORE_CLIENT_METADATA_STORE)
        const getAll = store.getAll()
        getAll.onsuccess = () => {
          const live = []
          const stale = []
          for (const record of getAll.result || []) {
            const updateTimeMs = Number(record?.updateTimeMs)
            const clientId = String(record?.clientId ?? '')
            if (Number.isFinite(updateTimeMs) && now - updateTimeMs <= maxAgeMs) live.push(clientId)
            else stale.push(clientId)
          }
          db.close()
          finish({ status: 'ok', live, stale })
        }
        getAll.onerror = () => {
          db.close()
          finish({ status: 'unavailable', live: [], stale: [], error: getAll.error || null })
        }
      } catch (error) {
        try {
          db.close()
        } catch {
          // nothing more to release
        }
        finish({ status: 'unavailable', live: [], stale: [], error })
      }
    }
  })
}

/**
 * Hold a passive connection to the Firestore database so this tab hears a peer's delete
 * request (`versionchange` with newVersion === null) and can get out of the way at once. The SDK
 * terminates its own client on that event, which would leave the page dead; the callback parks
 * and reloads it instead. Returns { close }; close it before this tab runs its own delete.
 */
export function watchFirestoreDatabaseDeletion({
  indexedDBApi = typeof indexedDB !== 'undefined' ? indexedDB : null,
  projectId = 'gse-main-site',
  onDeleteRequested,
  retryMs = 30 * 1000,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
} = {}) {
  let db = null
  let closed = false
  let retryTimer = null
  const name = firestoreMainDatabaseName(projectId)

  async function attach() {
    if (closed || !indexedDBApi?.open || typeof indexedDBApi.databases !== 'function') return
    let listed = false
    try {
      listed = (await indexedDBApi.databases()).some(database => database?.name === name)
    } catch {
      listed = false
    }
    if (closed) return
    if (!listed) {
      // Persistence has not created it yet (or it is being rebuilt); look again later.
      retryTimer = setTimeoutFn(attach, retryMs)
      return
    }
    let request
    try {
      request = indexedDBApi.open(name)
    } catch {
      return
    }
    request.onupgradeneeded = () => {
      try {
        request.transaction?.abort()
      } catch {
        // never create the database from here
      }
    }
    request.onerror = () => {}
    request.onblocked = () => {}
    request.onsuccess = () => {
      if (closed) {
        try {
          request.result.close()
        } catch {
          // already gone
        }
        return
      }
      db = request.result
      db.onversionchange = event => {
        if (event?.newVersion === null) {
          close()
          try {
            onDeleteRequested?.()
          } catch {
            // the caller's problem; the connection is already released
          }
        }
      }
      db.onclose = () => {
        db = null
      }
    }
  }

  function close() {
    closed = true
    if (retryTimer) clearTimeoutFn(retryTimer)
    retryTimer = null
    if (db) {
      try {
        db.close()
      } catch {
        // already gone
      }
      db = null
    }
  }

  attach()
  return { close }
}

function beginCacheReset(storage, now = Date.now()) {
  try {
    if (!storage) return false
    storage.setItem(
      FIRESTORE_CACHE_RESET_MARKER_KEY,
      JSON.stringify({ startedAt: now, expiresAt: now + RESET_MARKER_TTL_MS })
    )
    return storage.getItem(FIRESTORE_CACHE_RESET_MARKER_KEY) !== null
  } catch {
    return false
  }
}

function finishCacheReset(storage) {
  try {
    storage?.removeItem(FIRESTORE_CACHE_RESET_MARKER_KEY)
  } catch {
    // A failed cleanup expires automatically after RESET_MARKER_TTL_MS.
  }
}

// Escape hatch for a leaked reset marker (mPBqvuAO: a reload interrupted
// mid-reset strands it for the full 24h failsafe TTL). The background refresher
// clears it when it proves no reset is running (the lock is free and no
// persistent client exists) before a memory-mode tab returns to persistence.
export function clearFirestoreCacheResetMarker(storage = browserStorage('localStorage')) {
  finishCacheReset(storage)
}

// A real reset lasts seconds (drain ≤ 6 s, delete ≤ the boot budget). A marker older than
// RESET_MARKER_STALE_MS was PROBABLY left behind by a reload mid-reset. It is only half the
// proof: a delete that is still pending on a frozen peer's connection also outlives the marker's
// author, and opening the database behind that request is the C-09 freeze. Callers clear the
// marker only when countLiveFirestoreClients() also opens the database without blocking.
export const RESET_MARKER_STALE_MS = 60 * 1000
export function isFirestoreCacheResetMarkerStale(
  storage = browserStorage('localStorage'),
  now = Date.now(),
  maxAgeMs = RESET_MARKER_STALE_MS
) {
  try {
    const marker = safeJsonParse(storage?.getItem(FIRESTORE_CACHE_RESET_MARKER_KEY))
    if (!marker) return false
    const startedAt = Number(marker.startedAt)
    return !Number.isFinite(startedAt) || now - startedAt > maxAgeMs
  } catch {
    return false
  }
}

export function clearFirestoreMemoryFallback(storage = browserStorage('sessionStorage')) {
  volatileRecoveryState = null
  try {
    storage?.removeItem(FIRESTORE_MEMORY_FALLBACK_KEY)
  } catch {
    // Continue and clear the window.name fallback below.
  }
  try {
    if (window.name.startsWith(WINDOW_NAME_RECOVERY_PREFIX)) window.name = ''
  } catch {
    // No browser/window-name access; nothing else to clear.
  }
}

export function isFirestoreDatabaseName(name) {
  return typeof name === 'string' && name.startsWith(FIRESTORE_DATABASE_PREFIX)
}

export function clearFirestoreLocalStorageBookkeeping(storage = browserStorage('localStorage')) {
  if (!storage) return 0
  const keys = []
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (key?.startsWith(FIRESTORE_LOCAL_STORAGE_PREFIX)) keys.push(key)
  }
  for (const key of keys) storage.removeItem(key)
  return keys.length
}

function deleteFirestoreDatabase(indexedDBApi, name) {
  return new Promise(resolve => {
    let settled = false
    const finish = result => {
      if (settled) return
      settled = true
      resolve({ name, ...result })
    }

    let request
    try {
      request = indexedDBApi.deleteDatabase(name)
    } catch (error) {
      finish({ status: 'error', error })
      return
    }

    request.onsuccess = () => finish({ status: 'deleted' })
    request.onerror = () =>
      finish({ status: 'error', error: request.error || new Error(`Failed to delete ${name}`) })
    // `blocked` is a notification, not completion. Keep waiting for the real
    // success/error event; the outer bootstrap deadline will move the tab to
    // recovery mode if a peer never releases its connection.
    request.onblocked = () => {}
  })
}

/**
 * Delete Firestore persistence databases only. Firebase Auth and other
 * Firebase-owned IndexedDB databases are intentionally outside this scope.
 *
 * Refuses to start when Firestore client/mutation markers remain. Once a
 * request starts, `onblocked` is nonterminal; only actual success may be
 * followed by a cache-version advance.
 */
export async function clearFirestoreIndexedDB({
  indexedDBApi = typeof indexedDB !== 'undefined' ? indexedDB : null,
  localStorageApi = browserStorage('localStorage'),
  projectId = 'gse-main-site',
  countLiveClients = options => countLiveFirestoreClients({ indexedDBApi, projectId, ...options }),
} = {}) {
  if (!indexedDBApi?.deleteDatabase) {
    return { status: 'unsupported', deleted: [], blocked: [], errors: [], bookkeepingCleared: 0 }
  }

  // Last line of defence, re-proven here regardless of what the caller checked: no live client
  // (SDK clientMetadata), no pending delete, no unsynced write anywhere.
  const persistenceState = inspectFirestorePersistenceState(localStorageApi)
  const clients = await countLiveClients()
  const clientsClear =
    clients.status === 'no-database' || (clients.status === 'ok' && clients.live.length === 0)
  if (
    persistenceState.status !== 'ok' ||
    !clientsClear ||
    persistenceState.mutationKeys.length > 0
  ) {
    return {
      status: 'unsafe-to-delete',
      deleted: [],
      blocked: [],
      errors: [],
      bookkeepingCleared: 0,
      clientStatus: clients.status,
      liveClientIds: clients.live,
      pendingMutationKeys: persistenceState.mutationKeys,
    }
  }

  let names
  try {
    if (typeof indexedDBApi.databases === 'function') {
      const databases = await indexedDBApi.databases()
      names = databases.map(database => database?.name).filter(isFirestoreDatabaseName)
    } else {
      names = knownFirestoreDatabases(projectId)
    }
  } catch (error) {
    return {
      status: 'error',
      deleted: [],
      blocked: [],
      errors: [{ name: 'enumeration', error }],
      bookkeepingCleared: 0,
    }
  }

  const results = await Promise.all(
    [...new Set(names)].map(name => deleteFirestoreDatabase(indexedDBApi, name))
  )
  const deleted = results.filter(result => result.status === 'deleted').map(result => result.name)
  const blocked = []
  const errors = results.filter(result => result.status === 'error')

  if (blocked.length > 0) {
    return { status: 'blocked', deleted, blocked, errors, bookkeepingCleared: 0 }
  }
  if (errors.length > 0) {
    return { status: 'error', deleted, blocked, errors, bookkeepingCleared: 0 }
  }

  const bookkeepingCleared = clearFirestoreLocalStorageBookkeeping(localStorageApi)
  console.log(
    `[clearFirestoreCache] Cleared ${deleted.length} Firestore IndexedDB database(s) and ${bookkeepingCleared} local bookkeeping key(s)`
  )
  return { status: 'cleared', deleted, blocked, errors, bookkeepingCleared }
}

/**
 * The single destructive path (C-09), now run by the tabs themselves instead of by a user who
 * closed the others. A reset proceeds only under proof, read from the SDK's own bookkeeping: for
 * a persistent tab, exactly one live client (this one), pending writes drained, zero unsynced
 * write markers, this client terminated and the inventory re-proven empty; for a memory-mode tab
 * (which holds no database connection), zero live clients and zero unsynced write markers. A
 * database that cannot even be opened means a delete is already pending on it: never alone.
 * BroadcastChannel membership is deliberately not used as proof: frozen or old-bundle tabs may
 * never answer. No delete request is issued on any failed proof. Every result carries
 * `terminated`: true means this tab's Firestore instance is gone and the caller must reload.
 */
export function createFirestoreCacheResetCoordinator({
  drainPendingWrites,
  terminateFirestore,
  setCacheVersion,
  clearIndexedDB = clearFirestoreIndexedDB,
  // Live-client inventory (countLiveFirestoreClients). Injectable for the guard.
  countLiveClients = countLiveFirestoreClients,
  // Called under the lock before starting: a peer may have finished the reset while this tab
  // waited. Absent → always proceed.
  isStillPending = null,
  // Clears the shared pending-reset marker after a successful delete (cacheRefreshPolicy).
  clearPendingReset = null,
  lockManager = typeof navigator !== 'undefined' ? navigator.locks : null,
  localStorageApi = browserStorage('localStorage'),
  sessionStorageApi = browserStorage('sessionStorage'),
  pendingWriteDrainTimeoutMs = PENDING_WRITE_DRAIN_TIMEOUT_MS,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
} = {}) {
  if (typeof drainPendingWrites !== 'function') {
    throw new TypeError('drainPendingWrites is required')
  }
  if (typeof terminateFirestore !== 'function') {
    throw new TypeError('terminateFirestore is required')
  }
  if (typeof setCacheVersion !== 'function') {
    throw new TypeError('setCacheVersion is required')
  }

  let activeAttempt = null
  let abortRequested = false

  async function inventory() {
    const clients = await countLiveClients()
    const storage = inspectFirestorePersistenceState(localStorageApi)
    return {
      clientStatus: clients.status,
      live: clients.live || [],
      storageStatus: storage.status,
      mutationKeys: storage.mutationKeys,
    }
  }

  function unsafeResult(stage, inv, expectedClients) {
    const base = { stage, liveClientIds: inv.live, pendingMutationKeys: inv.mutationKeys }
    if (inv.storageStatus !== 'ok' || inv.clientStatus === 'unavailable') {
      return { status: 'client-inventory-unavailable', ...base }
    }
    if (inv.clientStatus === 'blocked') return { status: 'delete-pending', ...base }
    if (inv.clientStatus === 'no-database') {
      // Nothing to hold: fine for a memory-mode tab, impossible for a persistent one.
      return expectedClients === 0 ? null : { status: 'client-inventory-mismatch', ...base }
    }
    if (inv.live.length === expectedClients) return null
    return {
      // More clients than this tab accounts for: a peer holds the database. Fewer (a persistent
      // tab whose own record is missing): the inventory cannot be trusted either way.
      status: inv.live.length > expectedClients ? 'other-tabs-open' : 'client-inventory-mismatch',
      ...base,
    }
  }

  function pendingMutationResult(stage, inv) {
    return {
      status: 'pending-writes-remain',
      stage,
      liveClientIds: inv.live,
      pendingMutationKeys: inv.mutationKeys,
    }
  }

  async function performReset({ remoteVersion, ownsPersistentClient = true }) {
    if (abortRequested) return { status: 'aborted', stage: 'before-start', terminated: false }
    if (!beginCacheReset(localStorageApi)) {
      return { status: 'client-inventory-unavailable', terminated: false }
    }
    const attempt = { aborted: false }
    activeAttempt = attempt
    let terminated = false
    const done = result => ({ ...result, terminated })

    try {
      const expectedClients = ownsPersistentClient ? 1 : 0
      const beforeDrain = await inventory()
      const unsafeBefore = unsafeResult('before-drain', beforeDrain, expectedClients)
      if (unsafeBefore) return done(unsafeBefore)

      if (ownsPersistentClient) {
        try {
          await withCacheBootstrapTimeout(drainPendingWrites(), pendingWriteDrainTimeoutMs, {
            setTimeoutFn,
            clearTimeoutFn,
          })
        } catch (error) {
          return done({
            status:
              error instanceof CacheBootstrapTimeoutError
                ? 'pending-writes-timeout'
                : 'pending-writes-failed',
            error,
          })
        }
        if (abortRequested || attempt.aborted)
          return done({ status: 'aborted', stage: 'after-drain' })

        // waitForPendingWrites snapshots outstanding batches. Re-prove that no
        // second client appeared while it drained; otherwise a peer could have
        // appended a write after the snapshot.
        const afterDrain = await inventory()
        const unsafeAfter = unsafeResult('after-drain', afterDrain, 1)
        if (unsafeAfter) return done(unsafeAfter)
        if (afterDrain.mutationKeys.length > 0) {
          return done(pendingMutationResult('after-drain', afterDrain))
        }
        if (abortRequested || attempt.aborted) {
          return done({ status: 'aborted', stage: 'before-terminate' })
        }

        try {
          await terminateFirestore()
        } catch (error) {
          return done({ status: 'terminate-failed', error })
        }
        terminated = true
        if (abortRequested || attempt.aborted)
          return done({ status: 'aborted', stage: 'after-terminate' })
      } else if (beforeDrain.mutationKeys.length > 0) {
        // A memory-mode tab has nothing to drain; a dead tab's unsynced writes still block.
        return done(pendingMutationResult('before-delete', beforeDrain))
      }

      // Terminate removed this client's own metadata record; the inventory must now be empty.
      const beforeDelete = await inventory()
      const unsafeDelete = unsafeResult('before-delete', beforeDelete, 0)
      if (unsafeDelete) return done(unsafeDelete)
      if (beforeDelete.mutationKeys.length > 0) {
        return done(pendingMutationResult('before-delete', beforeDelete))
      }
      if (abortRequested || attempt.aborted)
        return done({ status: 'aborted', stage: 'before-delete' })

      const result = await clearIndexedDB()
      if (result.status !== 'cleared') return done(result)

      try {
        setCacheVersion(remoteVersion)
      } catch (error) {
        return done({ ...result, status: 'version-write-failed', error })
      }
      try {
        clearPendingReset?.()
      } catch {
        // A marker that survives only costs one more (harmless) reset later.
      }
      clearFirestoreMemoryFallback(sessionStorageApi)
      return done(result)
    } finally {
      if (activeAttempt === attempt) activeAttempt = null
      finishCacheReset(localStorageApi)
    }
  }

  async function reset(options) {
    abortRequested = false
    const runIfCurrent = () => {
      if (abortRequested) return { status: 'aborted', stage: 'before-start', terminated: false }
      if (typeof isStillPending === 'function' && !isStillPending(options?.remoteVersion)) {
        return { status: 'already-current', terminated: false }
      }
      return performReset(options || {})
    }
    if (typeof lockManager?.request === 'function') {
      return lockManager.request('gse-firestore-cache-reset', { mode: 'exclusive' }, runIfCurrent)
    }
    return runIfCurrent()
  }

  function abort() {
    abortRequested = true
    if (activeAttempt) activeAttempt.aborted = true
  }

  return { reset, abort, close: () => {} }
}
